import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Utensils, Search, Plus, Minus, Trash2, Send, Printer, ChefHat, ShoppingBag, Bike, Users, X, StickyNote, Divide } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { printKitchenTickets, printReceipt, printReceipts } from "@/lib/restaurantPrint";

const TYPES = [
  { k: "dine_in", l: "صالة", Icon: Users },
  { k: "takeaway", l: "سفري", Icon: ShoppingBag },
  { k: "delivery", l: "توصيل", Icon: Bike },
];

export default function RestaurantPOS() {
  const { currentCompany } = useAuth();
  const baseCurrency = (currentCompany?.base_currency || "SYP").toUpperCase();

  const [products, setProducts] = useState([]);
  const [rates, setRates] = useState({});
  const [tables, setTables] = useState([]);
  const [openTabs, setOpenTabs] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");

  const [orderType, setOrderType] = useState("dine_in");
  const [tableId, setTableId] = useState("");
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "" });
  const [cart, setCart] = useState([]); // unsent items
  const [currentTab, setCurrentTab] = useState(null);
  const [busy, setBusy] = useState(false);

  const [splitOpen, setSplitOpen] = useState(false);
  const [splitMode, setSplitMode] = useState("equal"); // "equal" | "items"
  const [splitCount, setSplitCount] = useState(2);
  const [lineGuest, setLineGuest] = useState({}); // line index -> guest number

  const convertAmount = (amt, from, to) => {
    const src = (from || baseCurrency).toUpperCase();
    const dst = (to || baseCurrency).toUpperCase();
    if (src === dst) return amt;
    const sr = src === baseCurrency ? 1 : Number(rates[src] || 0);
    const dr = dst === baseCurrency ? 1 : Number(rates[dst] || 0);
    if (!sr || !dr) return amt;
    return (amt * sr) / dr;
  };
  const priceInBase = (p) => {
    const v = convertAmount(Number(p.sale_price || 0), (p.currency || baseCurrency).toUpperCase(), baseCurrency);
    return Math.round(v * 100) / 100;
  };

  const loadProducts = async () => { try { const { data } = await api.get("/products"); setProducts(data || []); } catch (e) { toast.error(formatErr(e)); } };
  const loadRates = async () => { try { const { data } = await api.get("/currencies"); setRates(data?.rates || {}); } catch {} };
  const loadTables = async () => { try { const { data } = await api.get("/restaurant/tables"); setTables(data || []); } catch {} };
  const loadTabs = async () => { try { const { data } = await api.get("/restaurant/tabs", { params: { status: "open" } }); setOpenTabs(data || []); } catch {} };
  useEffect(() => { loadProducts(); loadRates(); loadTables(); loadTabs(); /* eslint-disable-next-line */ }, [currentCompany?.id]);

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => { if (p.category) set.add(p.category); });
    return ["all", ...Array.from(set)];
  }, [products]);

  const filtered = useMemo(() => {
    let list = products;
    if (activeCat !== "all") list = list.filter((p) => p.category === activeCat);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((p) => p.name.toLowerCase().includes(q) || (p.barcode || "").includes(search));
    return list.slice(0, 60);
  }, [products, activeCat, search]);

  const tableObj = tables.find((t) => t.id === tableId);

  const addToCart = (p) => {
    setCart((c) => {
      const i = c.findIndex((x) => x.product_id === p.id && !x.notes);
      if (i >= 0) { const n = [...c]; n[i] = { ...n[i], qty: n[i].qty + 1 }; return n; }
      return [...c, { product_id: p.id, name: p.name, qty: 1, price: priceInBase(p), notes: "", category: p.category || "" }];
    });
  };
  const incQty = (i, d) => setCart((c) => c.map((x, idx) => idx === i ? { ...x, qty: Math.max(1, x.qty + d) } : x));
  const setNote = (i, v) => setCart((c) => c.map((x, idx) => idx === i ? { ...x, notes: v } : x));
  const rmCart = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  const cartTotal = cart.reduce((s, l) => s + l.qty * l.price, 0);
  const sentTotal = (currentTab?.lines || []).reduce((s, l) => s + l.qty * l.price, 0);
  const grandTotal = cartTotal + sentTotal;

  const resetOrder = () => { setCart([]); setCurrentTab(null); setTableId(""); setCustomer({ name: "", phone: "", address: "" }); };

  const onPickTable = (id) => {
    setTableId(id);
    const openForTable = openTabs.find((t) => t.table_id === id && t.status === "open");
    setCurrentTab(openForTable || null);
    setCart([]);
  };

  const resumeTab = (tab) => {
    setOrderType(tab.order_type);
    setTableId(tab.table_id || "");
    setCustomer({ name: tab.customer_name || "", phone: tab.customer_phone || "", address: tab.customer_address || "" });
    setCurrentTab(tab);
    setCart([]);
  };

  const ensureTab = async () => {
    if (currentTab && currentTab.status === "open") return currentTab;
    if (orderType === "dine_in" && !tableId) { toast.error("اختر الطاولة أولاً"); return null; }
    const body = {
      order_type: orderType,
      table_id: orderType === "dine_in" ? (tableId || null) : null,
      table_number: tableObj?.number || "",
      customer_name: customer.name, customer_phone: customer.phone, customer_address: customer.address,
      currency: baseCurrency,
    };
    const { data } = await api.post("/restaurant/tabs", body);
    setCurrentTab(data);
    return data;
  };

  const sendToKitchen = async () => {
    if (!cart.length) { toast.error("أضف صنفاً واحداً على الأقل"); return null; }
    setBusy(true);
    try {
      const tab = await ensureTab();
      if (!tab) { setBusy(false); return null; }
      const { data } = await api.post(`/restaurant/tabs/${tab.id}/items`, { items: cart });
      setCurrentTab(data.tab);
      setCart([]);
      toast.success("تم إرسال الطلب للمطبخ");
      try { await printKitchenTickets(data.tickets || [], currentCompany?.id); } catch (e) {}
      loadTabs(); loadTables();
      return data.tab;
    } catch (e) { toast.error(formatErr(e)); return null; }
    finally { setBusy(false); }
  };

  const partyNameFor = (tab) => tab.customer_name || (tab.order_type === "dine_in" ? `طاولة ${tab.table_number || ""}`.trim() : "عميل نقدي");

  const buildInvoiceBody = (tab, lines, partyName, noteSuffix) => {
    const total = lines.reduce((s, l) => s + l.qty * l.price, 0);
    const typeLabel = { dine_in: "صالة", takeaway: "سفري", delivery: "توصيل" }[tab.order_type] || "";
    const notesParts = [typeLabel];
    if (tab.order_type === "dine_in" && tab.table_number) notesParts.push(`طاولة ${tab.table_number}`);
    if (tab.customer_phone) notesParts.push(tab.customer_phone);
    if (tab.customer_address) notesParts.push(tab.customer_address);
    if (noteSuffix) notesParts.push(noteSuffix);
    return {
      kind: "sale", party_id: null, party_name: partyName,
      payment_type: "cash", paid_amount: +total.toFixed(2),
      discount_percent: 0, discount_amount: 0, currency: baseCurrency, exchange_rate: 1,
      lines: lines.map((l) => ({
        product_id: l.product_id, product_name: l.name, quantity: l.qty, price: l.price,
        total: +(l.qty * l.price).toFixed(2), unit_name: "قطعة", unit_factor: 1,
      })),
      notes: notesParts.filter(Boolean).join(" · "),
      cash_box_id: null, bank_account_id: null, payments: null, return_reason: null, ref_invoice_id: null,
      warehouse_id: null, cost_center: "", vat_rate: null,
      idempotency_key: (window.crypto?.randomUUID && window.crypto.randomUUID()) || `rest-${tab.id}-${Date.now()}-${Math.random()}`,
    };
  };

  const payAndClose = async () => {
    setBusy(true);
    try {
      let tab = currentTab;
      if (cart.length) { tab = await sendToKitchen(); if (!tab) { setBusy(false); return; } }
      if (!tab || !(tab.lines || []).length) { toast.error("لا توجد أصناف في هذا الطلب"); setBusy(false); return; }
      const total = (tab.lines || []).reduce((s, l) => s + l.qty * l.price, 0);
      const { data: inv } = await api.post("/invoices", buildInvoiceBody(tab, tab.lines, partyNameFor(tab)));
      await api.post(`/restaurant/tabs/${tab.id}/pay`, {
        invoice_id: inv.id, invoice_number: inv.display_no || String(inv.number), payment_method: "cash",
      });
      toast.success(`تم إغلاق الطلب — فاتورة ${inv.display_no || inv.number}`);
      try { printReceipt({ company: currentCompany, tab, total, currency: baseCurrency, invoice_number: inv.display_no || inv.number }); } catch (e) {}
      resetOrder(); loadTabs(); loadTables();
    } catch (e) {
      if (e?.code === "OFFLINE_QUEUED") { toast.success("تم الحفظ محلياً — ستتم المزامنة عند عودة الإنترنت"); resetOrder(); loadTabs(); }
      else toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const openSplit = async () => {
    let tab = currentTab;
    if (cart.length) { tab = await sendToKitchen(); if (!tab) return; }
    if (!tab || !(tab.lines || []).length) { toast.error("أرسل الأصناف للمطبخ أولاً — لا توجد أصناف لتقسيمها"); return; }
    const init = {}; (tab.lines || []).forEach((_, i) => { init[i] = 1; });
    setLineGuest(init); setSplitCount(2); setSplitMode("equal"); setSplitOpen(true);
  };

  const confirmSplit = async () => {
    const tab = currentTab;
    if (!tab || !(tab.lines || []).length) { setSplitOpen(false); return; }
    const n = Math.max(2, Math.min(20, Number(splitCount) || 2));
    const total = (tab.lines || []).reduce((s, l) => s + l.qty * l.price, 0);
    setBusy(true);
    try {
      if (splitMode === "equal") {
        const { data: inv } = await api.post("/invoices", buildInvoiceBody(tab, tab.lines, partyNameFor(tab), `تقسيم بالتساوي على ${n} ضيوف`));
        await api.post(`/restaurant/tabs/${tab.id}/pay`, { invoice_id: inv.id, invoice_number: inv.display_no || String(inv.number), payment_method: `split_equal_${n}` });
        const per = Math.round((total / n) * 100) / 100;
        const slips = [];
        for (let i = 1; i <= n; i++) {
          const amt = i === n ? +(total - per * (n - 1)).toFixed(2) : per;
          slips.push({ company: currentCompany, tab, currency: baseCurrency, shareAmount: amt, guestLabel: `ضيف ${i} من ${n}` });
        }
        try { printReceipts(slips); } catch (e) {}
        toast.success(`تم الدفع وتقسيم ${fmtMoney(total, baseCurrency)} على ${n} ضيوف — فاتورة ${inv.display_no || inv.number}`);
      } else {
        const groups = {};
        (tab.lines || []).forEach((l, idx) => { const g = lineGuest[idx] || 1; (groups[g] = groups[g] || []).push(l); });
        const guestNos = Object.keys(groups).filter((g) => (groups[g] || []).length);
        if (!guestNos.length) { toast.error("عيّن الأصناف على الضيوف"); setBusy(false); return; }
        let firstInv = null; const slips = [];
        for (const g of guestNos) {
          const lines = groups[g];
          const gt = lines.reduce((s, l) => s + l.qty * l.price, 0);
          const { data: inv } = await api.post("/invoices", buildInvoiceBody(tab, lines, `${partyNameFor(tab)} - ضيف ${g}`, `تقسيم حسب الأصناف · ضيف ${g}`));
          if (!firstInv) firstInv = inv;
          slips.push({ company: currentCompany, tab: { ...tab, lines }, total: gt, currency: baseCurrency, invoice_number: inv.display_no || inv.number, guestLabel: `ضيف ${g}` });
        }
        await api.post(`/restaurant/tabs/${tab.id}/pay`, { invoice_id: firstInv.id, invoice_number: firstInv.display_no || String(firstInv.number), payment_method: `split_items_${guestNos.length}` });
        try { printReceipts(slips); } catch (e) {}
        toast.success(`تم دفع الحساب مقسّماً على ${guestNos.length} ضيوف`);
      }
      setSplitOpen(false); resetOrder(); loadTabs(); loadTables();
    } catch (e) {
      if (e?.code === "OFFLINE_QUEUED") { toast.success("تم الحفظ محلياً — ستتم المزامنة عند عودة الإنترنت"); setSplitOpen(false); resetOrder(); loadTabs(); }
      else toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const splitN = Math.max(2, Math.min(20, Number(splitCount) || 2));
  const splitTotal = (currentTab?.lines || []).reduce((s, l) => s + l.qty * l.price, 0);
  const splitPer = Math.round((splitTotal / splitN) * 100) / 100;
  const splitLast = +(splitTotal - splitPer * (splitN - 1)).toFixed(2);
  const guestTotals = (() => {
    const g = {};
    (currentTab?.lines || []).forEach((l, i) => { const key = lineGuest[i] || 1; g[key] = (g[key] || 0) + l.qty * l.price; });
    return Array.from({ length: splitN }, (_, k) => k + 1).map((gg) => ({ g: gg, amt: g[gg] || 0 }));
  })();

  return (
    <div className="space-y-3" data-testid="restaurant-pos-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><Utensils className="w-6 h-6 text-primary" /> كاشير المطعم</h1>
        {openTabs.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="open-tabs-bar">
            <span className="text-xs text-muted-foreground">طلبات مفتوحة:</span>
            {openTabs.map((t) => (
              <button key={t.id} onClick={() => resumeTab(t)} data-testid={`open-tab-${t.id}`}
                className={`text-xs px-2 py-1 rounded-full border ${currentTab?.id === t.id ? "bg-amber-500 text-white border-amber-500" : "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"}`}>
                {t.order_type === "dine_in" ? `طاولة ${t.table_number}` : (t.order_type === "takeaway" ? "سفري" : "توصيل")}
                {" · "}{Math.round((t.lines || []).reduce((s, l) => s + l.qty * l.price, 0)).toLocaleString("ar-EG")}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-3">
        {/* MENU */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute top-3 start-3 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ابحث عن صنف..." className="ps-9" data-testid="rpos-search" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {categories.map((c) => (
              <button key={c} onClick={() => setActiveCat(c)} data-testid={`rpos-cat-${c}`}
                className={`text-xs px-3 py-1.5 rounded-full transition ${activeCat === c ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted-foreground/10"}`}>
                {c === "all" ? "الكل" : c}
              </button>
            ))}
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground border-2 border-dashed rounded-lg">لا توجد أصناف — أضف أصنافاً من صفحة «الأصناف»</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {filtered.map((p) => (
                <button key={p.id} onClick={() => addToCart(p)} data-testid={`rpos-product-${p.id}`}
                  className="text-start border rounded-lg p-3 bg-card hover:border-primary hover:shadow-sm transition group">
                  <div className="font-bold text-sm line-clamp-2 min-h-[2.5rem]">{p.name}</div>
                  <div className="text-primary font-mono text-sm mt-1">{fmtMoney(priceInBase(p), baseCurrency)}</div>
                  {p.category && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{p.category}</div>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ORDER PANEL */}
        <div className="border rounded-lg bg-card p-3 space-y-3 h-fit lg:sticky lg:top-3">
          {/* Order type */}
          <div className="grid grid-cols-3 gap-1.5">
            {TYPES.map(({ k, l, Icon }) => (
              <button key={k} onClick={() => { setOrderType(k); if (k !== "dine_in") setTableId(""); }} data-testid={`rpos-type-${k}`}
                className={`flex flex-col items-center gap-1 py-2 rounded-lg text-xs font-semibold transition ${orderType === k ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted-foreground/10"}`}>
                <Icon className="w-4 h-4" /> {l}
              </button>
            ))}
          </div>

          {orderType === "dine_in" ? (
            <select className="w-full h-10 px-2 border rounded bg-background text-sm" value={tableId} onChange={(e) => onPickTable(e.target.value)} data-testid="rpos-table-select">
              <option value="">— اختر الطاولة —</option>
              {tables.map((t) => <option key={t.id} value={t.id}>{`طاولة ${t.number} (${t.capacity})`}</option>)}
            </select>
          ) : (
            <div className="space-y-1.5">
              <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="اسم العميل (اختياري)" className="h-9" data-testid="rpos-cust-name" />
              <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} placeholder="الهاتف (اختياري)" className="h-9" data-testid="rpos-cust-phone" />
              {orderType === "delivery" && <Input value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} placeholder="عنوان التوصيل (اختياري)" className="h-9" data-testid="rpos-cust-address" />}
            </div>
          )}

          {/* Already-sent items */}
          {currentTab && (currentTab.lines || []).length > 0 && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2" data-testid="rpos-sent-items">
              <div className="text-[11px] font-bold text-emerald-700 flex items-center gap-1 mb-1"><ChefHat className="w-3.5 h-3.5" /> مُرسل للمطبخ</div>
              {(currentTab.lines || []).map((l, i) => (
                <div key={i} className="flex justify-between text-xs py-0.5">
                  <span>{l.qty}× {l.name}{l.station_name ? <span className="text-emerald-600"> · {l.station_name}</span> : ""}</span>
                  <span className="font-mono">{(l.qty * l.price).toLocaleString("ar-EG")}</span>
                </div>
              ))}
            </div>
          )}

          {/* Cart (unsent) */}
          <div className="min-h-[80px]">
            {cart.length === 0 ? (
              <div className="text-center text-xs text-muted-foreground py-6">اختر أصنافاً من القائمة لإضافتها</div>
            ) : (
              <div className="space-y-1.5" data-testid="rpos-cart">
                {cart.map((l, i) => (
                  <div key={i} className="border rounded-lg p-2" data-testid={`rpos-cart-item-${i}`}>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 text-sm font-semibold">{l.name}</div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => incQty(i, -1)} className="w-6 h-6 rounded bg-muted grid place-items-center" data-testid={`rpos-dec-${i}`}><Minus className="w-3 h-3" /></button>
                        <span className="w-6 text-center text-sm font-bold">{l.qty}</span>
                        <button onClick={() => incQty(i, 1)} className="w-6 h-6 rounded bg-muted grid place-items-center" data-testid={`rpos-inc-${i}`}><Plus className="w-3 h-3" /></button>
                      </div>
                      <div className="w-16 text-end font-mono text-sm">{(l.qty * l.price).toLocaleString("ar-EG")}</div>
                      <button onClick={() => rmCart(i)} className="text-red-500" data-testid={`rpos-rm-${i}`}><X className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <StickyNote className="w-3 h-3 text-muted-foreground" />
                      <input value={l.notes} onChange={(e) => setNote(i, e.target.value)} placeholder="ملاحظة (بدون بصل، حار...)" className="flex-1 text-xs bg-transparent border-b outline-none py-0.5" data-testid={`rpos-note-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Totals + actions */}
          <div className="border-t pt-2 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">جديد</span><span className="font-mono">{fmtMoney(cartTotal, baseCurrency)}</span></div>
            {sentTotal > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">مُرسل</span><span className="font-mono">{fmtMoney(sentTotal, baseCurrency)}</span></div>}
            <div className="flex justify-between font-bold text-lg"><span>الإجمالي</span><span className="font-mono text-primary" data-testid="rpos-total">{fmtMoney(grandTotal, baseCurrency)}</span></div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={sendToKitchen} disabled={busy || cart.length === 0} className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="rpos-send-btn">
                <Send className="w-4 h-4" /> إرسال للمطبخ
              </Button>
              <Button onClick={payAndClose} disabled={busy || grandTotal <= 0} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="rpos-pay-btn">
                <Printer className="w-4 h-4" /> إغلاق ودفع
              </Button>
            </div>
            {(sentTotal > 0 || cart.length > 0) && (
              <Button variant="outline" onClick={openSplit} disabled={busy || grandTotal <= 0} className="w-full gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="rpos-split-btn">
                <Divide className="w-4 h-4" /> تقسيم الحساب بين عدة عملاء
              </Button>
            )}
            {(cart.length > 0 || currentTab) && (
              <Button variant="ghost" onClick={resetOrder} className="w-full h-8 text-xs text-muted-foreground" data-testid="rpos-new-btn">طلب جديد / إلغاء التحديد</Button>
            )}
          </div>
        </div>
      </div>

      <Dialog open={splitOpen} onOpenChange={setSplitOpen}>
        <DialogContent data-testid="rpos-split-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Divide className="w-5 h-5 text-indigo-600" /> تقسيم الحساب</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">اختر طريقة التقسيم وعدد الضيوف، ثم ادفع واطبع حصة كل ضيف</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setSplitMode("equal")} data-testid="rpos-split-mode-equal"
                className={`py-2 rounded-lg text-sm font-semibold border transition ${splitMode === "equal" ? "bg-indigo-600 text-white border-indigo-600" : "bg-muted hover:bg-muted-foreground/10"}`}>بالتساوي</button>
              <button onClick={() => setSplitMode("items")} data-testid="rpos-split-mode-items"
                className={`py-2 rounded-lg text-sm font-semibold border transition ${splitMode === "items" ? "bg-indigo-600 text-white border-indigo-600" : "bg-muted hover:bg-muted-foreground/10"}`}>حسب الأصناف</button>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground flex items-center gap-1"><Users className="w-4 h-4" /> عدد الضيوف</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setSplitCount((c) => Math.max(2, Number(c) - 1))} className="w-8 h-8 rounded bg-muted grid place-items-center" data-testid="rpos-split-dec"><Minus className="w-4 h-4" /></button>
                <input type="number" min={2} max={20} value={splitCount} onChange={(e) => setSplitCount(e.target.value)} className="w-14 h-8 text-center border rounded bg-background" data-testid="rpos-split-count" />
                <button onClick={() => setSplitCount((c) => Math.min(20, Number(c) + 1))} className="w-8 h-8 rounded bg-muted grid place-items-center" data-testid="rpos-split-inc"><Plus className="w-4 h-4" /></button>
              </div>
            </div>

            {splitMode === "equal" ? (
              <div className="rounded-lg border p-3 text-sm space-y-1.5" data-testid="rpos-split-equal-preview">
                <div className="flex justify-between"><span className="text-muted-foreground">الإجمالي</span><span className="font-mono font-bold">{fmtMoney(splitTotal, baseCurrency)}</span></div>
                <div className="flex justify-between text-indigo-700"><span>حصة كل ضيف ({splitN})</span><span className="font-mono font-bold" data-testid="rpos-split-per">{fmtMoney(splitPer, baseCurrency)}</span></div>
                {splitLast !== splitPer && <div className="text-xs text-muted-foreground">آخر ضيف يدفع {fmtMoney(splitLast, baseCurrency)} (فرق التقريب)</div>}
              </div>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="rpos-split-items">
                {(currentTab?.lines || []).map((l, i) => (
                  <div key={i} className="flex items-center gap-2 border rounded p-2 text-sm">
                    <div className="flex-1 truncate">{l.qty}× {l.name}</div>
                    <div className="font-mono text-xs w-16 text-end">{(l.qty * l.price).toLocaleString("ar-EG")}</div>
                    <select value={lineGuest[i] || 1} onChange={(e) => setLineGuest((m) => ({ ...m, [i]: Number(e.target.value) }))} className="h-8 border rounded px-1 text-xs bg-background" data-testid={`rpos-split-line-${i}`}>
                      {Array.from({ length: splitN }, (_, k) => k + 1).map((g) => <option key={g} value={g}>{`ضيف ${g}`}</option>)}
                    </select>
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-1 pt-1">
                  {guestTotals.map(({ g, amt }) => (
                    <div key={g} className="flex justify-between items-center gap-2 text-xs bg-muted/40 rounded px-2 py-1" data-testid={`rpos-split-guest-total-${g}`}><span>ضيف {g}</span><span className="font-mono">{amt.toLocaleString("ar-EG")}</span></div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitOpen(false)}>إلغاء</Button>
            <Button onClick={confirmSplit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="rpos-split-confirm"><Printer className="w-4 h-4" /> دفع وطباعة الحصص</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
